import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
/**
 * Podstrona „O nas" — /o-nas
 * Placeholdery na zdjęcia zespołu, teksty i galerię.
 * Karta pracownika wyświetla się TYLKO jeśli obiekt jest w tablicy TEAM.
 */
import { useState, type MouseEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'wouter';
import { MapPin, X, ArrowRight, Flower2, Heart, Star, Sprout } from 'lucide-react';
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { BUKIETOWA_LOCATIONS } from '@/lib/globalFloristSchema';
import { SectionLabel as Tag } from '@/components/ui/SectionLabel';

/* ── Tokens ─────────────────────────────────────────────── */
const GOLD   = '#e05590';
const DARK   = '#2a1f14';
const MUTED  = 'rgba(42,31,20,0.50)';
const LINE   = 'rgba(210,60,120,0.18)';
const CREAM  = '#fff';
const CREAM2 = '#fff';
const CREAM3 = '#fff';

const ease = [0.22, 1, 0.36, 1] as const;
const fadeUp = (delay = 0) => ({
  initial:     { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport:    { once: true, margin: '-40px' },
  transition:  { duration: 0.6, delay, ease },
});

/* ══════════════════════════════════════════════════════════
   DANE — uzupełnij poniższe tablice właściwymi danymi
══════════════════════════════════════════════════════════ */

/**
 * Tekst sekcji „O Bukietowej" — zastąp właściwą treścią.
 * Każdy element tablicy to osobny akapit.
 */
const ABOUT_PARAGRAPHS: string[] = [
  // Wstaw własne akapity. Przykład:
  // 'Bukietowa to rodzinna kwiaciarnia działająca w Lublinie od ... roku.',
  // 'Codziennie tworzymy kompozycje, które ...',
];

/**
 * Pracownicy — dodaj obiekty zgodnie ze strukturą poniżej.
 * Zostaw tablicę pustą, dopóki nie masz danych.
 * Karty wyświetlają się TYLKO gdy lista nie jest pusta.
 *
 * Przykład:
 * {
 *   name: 'Anna Kowalska',
 *   role: 'Florystka',
 *   location: 'al. Warszawska 23/1',
 *   photo: '/photos/team/anna.jpg',   // ścieżka do zdjęcia z public/
 * }
 */
interface TeamMember { name: string; role: string; location: string; photo?: string }
const TEAM: TeamMember[] = [
  // Dodaj pracowników tutaj
];

/**
 * Galeria codzienności — dodaj ścieżki do zdjęć z public/.
 * Każdy element: { src, alt, aspect } gdzie aspect to np. '3/4' | '4/3' | '1/1'
 * Zostaw pustą, dopóki nie masz zdjęć.
 */
interface GalleryPhoto { src: string; alt: string; aspect?: string }
const GALLERY: GalleryPhoto[] = [
  // { src: '/photos/team/praca1.jpg', alt: 'Opis zdjęcia', aspect: '3/4' },
  // { src: '/photos/team/praca2.jpg', alt: 'Opis zdjęcia', aspect: '4/3' },
  // { src: '/photos/team/praca3.jpg', alt: 'Opis zdjęcia', aspect: '1/1' },
];

/* ── Lokalizacje ────────────────────────────────────────── */
const LOCATIONS = BUKIETOWA_LOCATIONS.map(location => ({
  name: `Kwiaciarnia - ${location.address.streetAddress}`,
  address: `${location.address.streetAddress}, ${location.address.addressLocality}`,
  phone: location.telephone,
}));

/* ── Wartości ───────────────────────────────────────────── */
const VALUES = [
  { icon: Sprout,  title: 'Świeżość',  desc: 'Dbamy o jakość i świeżość kwiatów.' },
  { icon: Flower2, title: 'Florystyka', desc: 'Każdą kompozycję tworzymy z dbałością o szczegóły.' },
  { icon: Heart,   title: 'Relacje',    desc: 'Zależy nam na dobrym kontakcie i zrozumieniu potrzeb Klientów.' },
  { icon: MapPin,  title: 'Lokalność',  desc: 'Jesteśmy lokalną kwiaciarnią działającą w Lublinie i okolicach.' },
];

/* ══════════════════════════════════════════════════════════
   Shared primitives
══════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════
   1. HERO
══════════════════════════════════════════════════════════ */
function HeroSection() {
  return (
    <section style={{ background: '#fff', minHeight: 'calc(52dvh - 45px)', display: 'flex', alignItems: 'center', overflow: 'hidden', position: 'relative' }}>
      <PageBreadcrumb crumbs={[{ label: 'O nas' }]} />
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: 'calc(var(--page-breadcrumb-height, 44px) + 12px) var(--page-gutter) 24px' }}>
        <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px,5vw,60px)' }}>
            <h1 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(3rem,6vw,5.5rem)', fontWeight: 400, color: DARK, margin: '0 0 24px', lineHeight: 1.02 }}><CmsEditorialText id="editorial.HeroSection.h1.1" value={"Poznajmy się"} section="Wstęp" label="Nagłówek 1" /></h1>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px,1.5vw,16.5px)', color: MUTED, lineHeight: 1.85, margin: '0 auto', maxWidth: 600 }}><CmsEditorialText id="editorial.HeroSection.p.1" value={"Bukietowa to nie tylko kwiaty. To przede wszystkim ludzie, którzy każdego dnia wkładają serce w tworzenie pięknych kompozycji i sprawianie radości naszym Klientom."} section="Wstęp" label="Akapit 1" multiline /></p>
          </div>
        </motion.div>

      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════
   2. O BUKIETOWEJ
══════════════════════════════════════════════════════════ */
function AboutSection() {
  if (ABOUT_PARAGRAPHS.length === 0) return null;
  return (
    <section style={{ background: '#fff', padding: 'clamp(64px,8vw,104px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,420px),1fr))', gap: 'clamp(40px,6vw,80px)', alignItems: 'center' }}>

          {/* Tekst */}
          <motion.div {...fadeUp()}>
            <Tag text="Historia" />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3.2rem)', fontWeight: 400, color: DARK, margin: '0 0 24px', lineHeight: 1.1 }}><CmsEditorialText id="editorial.AboutSection.h2.1" value={"Kwiaty tworzą ludzie"} section="O Bukietowej" label="Nagłówek 1" /></h2>
            {ABOUT_PARAGRAPHS.map((paragraph, i) => (
              <p key={i} style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 15, color: MUTED, lineHeight: 1.9, margin: '0 0 18px' }}>{paragraph}</p>
            ))}
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════
   3. ZESPÓŁ
══════════════════════════════════════════════════════════ */
function TeamSection() {
  if (TEAM.length === 0) return null;

  return (
    <section style={{ background: '#fff', padding: 'clamp(64px,8vw,104px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 'clamp(40px,5vw,56px)' }}>
          <Tag text="Ludzie" />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3.2rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            <CmsEditorialText id="editorial.TeamSection.h2.2" value={"Poznaj nasz zespół"} section="Zespół" label="Nagłówek 2" />
          </h2>
        </motion.div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(min(100%,300px),1fr))',
          gap: 'clamp(16px,2vw,28px)',
        }}>
          {TEAM.map((member, i) => (
            <motion.article key={i} {...fadeUp(i * 0.07)}
              style={{ background: CREAM, overflow: 'hidden' }}
            >
              {/* Zdjęcie */}
              <div style={{ aspectRatio: '3/4', overflow: 'hidden', background: '#fff', position: 'relative' }}>
                {member.photo ? (
                  <img
                    src={member.photo}
                    alt={member.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
                  />
                ) : (
                  <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <Star size={18} color={`${GOLD}55`} />
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: `${GOLD}77` }}>
                      Zdjęcie
                    </span>
                  </div>
                )}
              </div>
              {/* Info */}
              <div style={{ padding: 'clamp(20px,2.5vw,28px)', borderTop: `2px solid ${GOLD}` }}>
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(19px,2vw,24px)', fontWeight: 400, color: DARK, margin: '0 0 5px', lineHeight: 1.2 }}>
                  {member.name || <span style={{ color: `${DARK}30` }}>Imię i nazwisko</span>}
                </h3>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: MUTED, margin: '0 0 14px', letterSpacing: '0.04em' }}>
                  {member.role || <span style={{ color: `${DARK}30`, fontStyle: 'italic' }}>Stanowisko / rola</span>}
                </p>
                {member.location && (
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11.5, color: GOLD, margin: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <MapPin size={11} /> {member.location}
                  </p>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════
   4. LOKALIZACJE
══════════════════════════════════════════════════════════ */
function LocationsSection() {
  return (
    <section style={{ background: '#fff', padding: 'clamp(64px,8vw,104px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 'clamp(40px,5vw,56px)' }}>
          <Tag text="Lokalizacje" />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3.2rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            Spotkasz nas tutaj
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,340px),1fr))', gap: 'clamp(16px,2vw,28px)' }}>
          {LOCATIONS.map((loc, i) => (
            <motion.div key={i} {...fadeUp(i * 0.1)}
              style={{ background: '#fff', padding: 'clamp(28px,3vw,44px)', borderTop: `2px solid ${GOLD}` }}
            >
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: `${GOLD}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                <MapPin size={16} color={GOLD} />
              </div>
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(18px,2vw,22px)', fontWeight: 400, color: DARK, margin: '0 0 10px', lineHeight: 1.25 }}>
                {loc.name}
              </h3>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: MUTED, margin: '0 0 6px' }}>
                {loc.address}
              </p>
              <a href={`tel:${loc.phone.replace(/\s/g,'')}`} style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: GOLD, textDecoration: 'none' }}>
                {loc.phone}
              </a>
              {/* Opcjonalne: kto tu pracuje */}
              {/* <p style={{ fontFamily: 'DM Sans', fontSize: 12, color: MUTED, marginTop: 14 }}>
                Znajdziesz tu: {loc.team}
              </p> */}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════
   5. GALERIA — editorial / masonry
══════════════════════════════════════════════════════════ */
function GallerySection() {
  const [lightbox, setLightbox] = useState<GalleryPhoto | null>(null);

  if (GALLERY.length === 0) return null;
  const slots = GALLERY;

  return (
    <section style={{ background: '#fff', padding: 'clamp(64px,8vw,104px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 'clamp(40px,5vw,56px)' }}>
          <Tag text="Codzienność" />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3.2rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            <CmsEditorialText id="editorial.GallerySection.h2.1" value={"Nasza codzienność"} section="Galeria" label="Nagłówek 1" />
          </h2>
        </motion.div>

          {/* Galeria */}
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12,1fr)', gap: 'clamp(8px,1.2vw,16px)' }}>
              {slots.map((photo, i) => {
                // Editorial column spans: 1→5col tall, 2→7col, 3→5col, 4→6col, 5→6col
                const spans = ['span 5', 'span 7', 'span 5', 'span 6', 'span 6'];
                const span  = spans[i % spans.length] ?? 'span 6';
                return (
                  <motion.button
                    key={i}
                    {...fadeUp(i * 0.06)}
                    onClick={() => setLightbox(photo)}
                    style={{ gridColumn: span, padding: 0, border: 'none', cursor: 'pointer', overflow: 'hidden', aspectRatio: photo.aspect ?? '4/3', position: 'relative', background: CREAM2 }}
                    whileHover={{ scale: 1.015 }}
                    transition={{ duration: 0.35, ease }}
                    aria-label={`Otwórz zdjęcie: ${photo.alt}`}
                  >
                    <img src={photo.src} alt={photo.alt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </motion.button>
                );
              })}
            </div>

            {/* Lightbox */}
            <AnimatePresence>
              {lightbox && (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  onClick={() => setLightbox(null)}
                  style={{ position: 'fixed', inset: 0, background: 'rgba(42,31,20,0.88)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
                >
                  <motion.div
                    initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
                    transition={{ duration: 0.25, ease }}
                    onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                    style={{ position: 'relative', maxWidth: '90vw', maxHeight: '88vh' }}
                  >
                    <img src={lightbox.src} alt={lightbox.alt} style={{ display: 'block', maxWidth: '90vw', maxHeight: '88vh', objectFit: 'contain' }} />
                    <button
                      onClick={() => setLightbox(null)}
                      aria-label="Zamknij"
                      style={{ position: 'absolute', top: -16, right: -16, width: 36, height: 36, borderRadius: '50%', background: CREAM, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <X size={14} color={DARK} />
                    </button>
                    {lightbox.alt && (
                      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'rgba(255,255,255,0.6)', margin: '10px 0 0', textAlign: 'center' }}>
                        {lightbox.alt}
                      </p>
                    )}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════
   6. NASZE PODEJŚCIE
══════════════════════════════════════════════════════════ */
function ValuesSection() {
  return (
    <section style={{ background: '#fff', padding: 'clamp(64px,8vw,104px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 'clamp(40px,5vw,56px)' }}>
          <Tag text="Wartości" />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3.2rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            <CmsEditorialText id="editorial.ValuesSection.h2.1" value={"Co jest dla nas ważne?"} section="Wartości" label="Nagłówek 1" />
          </h2>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,240px),1fr))', gap: 1, background: LINE }}>
          {VALUES.map((v, i) => {
            const Icon = v.icon;
            return (
              <motion.div key={v.title} {...fadeUp(i * 0.08)}
                style={{ background: '#fff', padding: 'clamp(28px,3vw,44px)', borderTop: `2px solid ${GOLD}` }}
              >
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: `${GOLD}10`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                  <Icon size={17} color={GOLD} />
                </div>
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(19px,2vw,23px)', fontWeight: 400, color: DARK, margin: '0 0 10px', lineHeight: 1.2 }}>
                  <CmsEditorialText id={`information.values.items.${i}.title`} value={v.title} section="Wartości" label={`Element ${i + 1}: tytuł`} />
                </h3>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: MUTED, lineHeight: 1.75, margin: 0 }}>
                  <CmsEditorialText id={`information.values.items.${i}.desc`} value={v.desc} section="Wartości" label={`Element ${i + 1}: opis`} multiline />
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════
   7. CTA
══════════════════════════════════════════════════════════ */
function CtaSection() {
  return (
    <section style={{ background: '#fff', padding: 'clamp(72px,9vw,112px) 0', borderTop: `1px solid ${LINE}` }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 clamp(24px,5vw,80px)', textAlign: 'center' }}>
        <motion.div {...fadeUp()}>
          <Tag text="Kontakt" />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.8vw,3.4rem)', fontWeight: 400, color: DARK, margin: '0 0 16px', lineHeight: 1.1 }}><CmsEditorialText id="editorial.CtaSection.h2.1" value={"Masz pytanie?"} section="Zachęta do kontaktu" label="Nagłówek 1" /><br />
            <em style={{ color: GOLD, fontStyle: 'italic' }}><CmsEditorialText id="editorial.CtaSection.em.1" value={"Poznajmy się bliżej."} section="Zachęta do kontaktu" label="Nagłówek 1" /></em>
          </h2>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px,1.4vw,15.5px)', color: MUTED, lineHeight: 1.85, margin: '0 0 36px' }}>
            <CmsEditorialText id="editorial.CtaSection.p.1" value={"Jeśli chcesz dowiedzieć się więcej o naszej ofercie albo potrzebujesz pomocy w wyborze kwiatów, zapraszamy do kontaktu."} section="Zachęta do kontaktu" label="Akapit 1" multiline />
          </p>
          <Link
            href="/kontakt/"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
              color: '#fff', background: DARK, padding: '15px 36px', textDecoration: 'none',
            }}
          >
            Skontaktuj się z nami <ArrowRight size={14} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════
   Page entry
══════════════════════════════════════════════════════════ */
export default function ONasPage() {
  usePrerenderReady();
  useSeo(PAGE_SEO.oNas);
  return (
    <div style={{ background: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main style={{ flex: 1 }}>
        <HeroSection />
        <AboutSection />
        <TeamSection />
        <LocationsSection />
        <GallerySection />
        <ValuesSection />
        <CtaSection />
      </main>
      <Footer />
    </div>
  );
}
